import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Route, Routes, useSearchParams, useLocation } from 'react-router-dom';
import loadable from '@loadable/component';
import './App.scss';

import { WebSocketContextProvider } from './components/WebSocket/WebSocket';
import { UserContextProvider } from './components/Context/UserContext';
import { TreeContextProvider } from './components/Context/TreeContext';
import Constants from './utils/Constants';

const Login = loadable(() => import('./components/Login'));
const PolicyVerification = loadable(() => import('./components/HR/SignUp/PolicyVerification'));
const FormSignUp = loadable(() => import('./components/HR/SignUp/FormSignUp'));

const JobPosting = loadable(() => import('./components/HR/JobPosting'));
const ManageJobPosting = loadable(() => import('./components/HR/Management/ManageJobPosting'));
const ViewEditManageJobPosting = loadable(() => import('./components/HR/Management/ViewEditManageJobPosting'));
const ManageApplicant = loadable(() => import('./components/HR/Management/ManageApplicant'));
const Applicant = loadable(() => import('./components/HR/Applicant'));
const AddEditJobPosting = loadable(() => import('./components/HR/JobPosting/AddEditJobPosting'));
const AddEditJobPostingForm = loadable(() => import('./components/HR/JobPosting/AddEditJobPostingForm'));
const Authority = loadable(() => import('./components/HR/Authority'));
const WorkManagement = loadable(() => import('./components/HR/Authority/WorkManagerTable'));
const LeaveManagement = loadable(() => import('./components/HR/LeaveManagement'));
const HRCard = loadable(() => import('./components/HR/HRCard'));
const MobileViewHr = loadable(() => import('./components/HR/HRCard/MobileViewHR'));
const AppChat = loadable(() => import('./components/HR/AppChat'));
const AddEmployee = loadable(() => import('./components/HR/Authority/AddEmployee'));
const LoginRedirectCallBack = loadable(() => import('./components/Login/LoginRedirectCallBack'));
const NavBar = loadable(() => import('./components/HR/NavBar'));
const TopRightBar = loadable(() => import('./components/HR/NavBar/TopRightBar'));
const BannerAlert = loadable(() => import('./components/HR/BannerAlert'));
const ResumePage = loadable(() => import('./components/HR/Authority/ResumePage'));
const MyCareerCard = loadable(() => import('./components/MyCareer/MyCareerCard'));

const AddEditEmployee = loadable(() => import('./components/HR/HRCard/HrCardDetail/AddEditForm/AddEditEmployeeForm'));
const AddWork = loadable(() => import('./components/HR/WorkManagement/AddWork'));
const ViewSchedule = loadable(() => import('./components/HR/WorkManagement/ViewWork/ViewSchedule'));
const NewWork = loadable(() => import('./components/HR/WorkManagement/NewWork/ViewSchedule'));
const EditWork = loadable(() => import('./components/HR/WorkManagement/NewWork/EditWork'));
const TimeListPicker = loadable(() => import('./components/HR/WorkManagement/NewWork/EditWork/TimeListPicker'));
const PlaceListPicker = loadable(() => import('./components/HR/WorkManagement/NewWork/EditWork/PlaceListPicker'));
const ViewWork = loadable(() => import('./components/HR/WorkManagement/ViewWork'));

const LeaveStatus = loadable(() => import('./components/HR/AnnualLeave/LeaveStatus'));
const LeaveRequest = loadable(() => import('./components/HR/AnnualLeave/LeaveRequest'));
const LeaveHistory = loadable(() => import('./components/HR/AnnualLeave/LeaveHistory'));
const LeaveView = loadable(() => import('./components/HR/AnnualLeave/LeaveView'));

const CompanyForm = loadable(() => import('./components/HR/Setting/CompanyForm'));
const ProfilePage = loadable(() => import('./components/HR/ProfilePage'));
const ConfirmDeleteAccount = loadable(() => import('./components/HR/ProfilePage/ConfirmDeleteAccount'));

const MemoizedNavBar = React.memo(NavBar);

function App() {
    const [hovering, setHovering] = useState(false);
    const [isExpandNavBar, setIsExpandNavBar] = useState(true);
    const [searchParams] = useSearchParams();
    const location = useLocation();
    let isHideHeader = searchParams.get('isHideHeader');

    useEffect(() => {
        let token = searchParams.get('token');
        if (token) {
            sessionStorage.setItem(Constants.LOCAL_STORAGE.AUTHORIZATION, token);
            sessionStorage.setItem(Constants.LOCAL_STORAGE.TOKEN, token);
        }
    }, [searchParams]);

    const handleExpandNavBar = (isExpand) => {
        setIsExpandNavBar(isExpand);
    };

    const handleHovering = useCallback((isHovering) => {
        setHovering(isHovering);
    }, []);

    const isDisplayFullPage = () => {
        return location.pathname.includes('profilePage') || location.pathname.includes('addEditEmployee');
    };

    const isDisplayTopRightBar = () => {
        return !location.pathname.includes('confirmDeleteAccount');
    };

    const routes = useMemo(
        () => (
            <Routes>
                <Route path="/">
                    <Route path="" element={<Login />} />
                    <Route path="signUp">
                        <Route path="policyVerification" element={<PolicyVerification />} />
                        <Route path="formSignUp" element={<FormSignUp />} />
                    </Route>
                    <Route path="appChat" element={<AppChat />} />
                    <Route path="profilePage">
                        <Route path="" element={<ProfilePage />} />
                        <Route path="confirmDeleteAccount" element={<ConfirmDeleteAccount />} />
                    </Route>
                    <Route path="webLoginCallback" element={<LoginRedirectCallBack />} />
                    <Route path="resumePage" element={<ResumePage />} />
                    <Route path="myCareerCard" element={<MyCareerCard />} />
                    <Route path="recruitment">
                        <Route path="jobPosting">
                            <Route path="" element={<JobPosting />} />
                            <Route path="addEditJobPosting" element={<AddEditJobPosting />} />
                            <Route path="AddEditJobPostingForm" element={<AddEditJobPostingForm />} />
                        </Route>
                        <Route path="applicant" element={<Applicant />} />
                    </Route>
                    <Route path="management">
                        <Route path="jobPosting">
                            <Route path="" element={<ManageJobPosting />} />
                            <Route path="addEditJobPosting" element={<ViewEditManageJobPosting />} />
                        </Route>
                        <Route path="applicant" element={<ManageApplicant />} />
                    </Route>
                    <Route path="setting">
                        <Route path="company" element={<CompanyForm />} />
                    </Route>
                    <Route path="company">
                        <Route path="hrCardDetail">
                            <Route
                                path=""
                                element={
                                    <TreeContextProvider>
                                        <HRCard />
                                    </TreeContextProvider>
                                }
                            />
                            <Route path="addEditEmployee/:id" element={<AddEditEmployee />} />
                            <Route path="addEmployee/:id" element={<AddEmployee />} />
                        </Route>

                        <Route path="mobileViewHr">
                            <Route
                                path=""
                                element={
                                    <TreeContextProvider>
                                        <MobileViewHr />
                                    </TreeContextProvider>
                                }
                            />
                            <Route path="addEmployee/:id" element={<AddEmployee />} />
                        </Route>
                        <Route path="authority">
                            <Route path="" element={<Authority />} />
                        </Route>
                        <Route path="workManagement" element={<WorkManagement />} />
                        <Route path="leaveManagement">
                            <Route path="" element={<LeaveManagement />} />
                        </Route>
                        <Route path="addWork">
                            <Route path="" element={<AddWork />} />
                        </Route>
                        <Route path="viewSchedule">
                            <Route path="" element={<ViewSchedule />}>
                                <Route path="viewWork/:id" element={<ViewWork />} />
                            </Route>
                        </Route>
                        <Route path="newWork">
                            <Route path="" element={<NewWork />} />
                            <Route path="editWork" element={<EditWork />} />
                            <Route path="timeListPicker" element={<TimeListPicker />} />
                            <Route path="placeListPicker" element={<PlaceListPicker />} />
                        </Route>
                    </Route>
                    <Route path="/annualLeave">
                        <Route path="leaveStatus" element={<LeaveStatus />} />
                        <Route path="leaveRequest/:topicId" element={<LeaveRequest />} />
                        <Route path="leaveView" element={<LeaveView />} />
                        <Route path="leaveHistory/:topicId" element={<LeaveHistory />} />
                    </Route>
                </Route>
            </Routes>
        ),
        []
    );

    return (
        <UserContextProvider>
            <WebSocketContextProvider>
                <div className={`${isExpandNavBar ? 'expand' : 'collapse'}-sidebar-desktop ${hovering || isExpandNavBar ? '' : 'shrink-mode'} ${isDisplayFullPage() ? 'full-page' : ''}`}>
                    {/* {!isHideHeader && <MemoizedNavBar expandNavBar={handleExpandNavBar} isExpandNavBar={isExpandNavBar} setIsHovering={handleHovering} />} */}
                    {true && <TopRightBar />}
                    {routes}
                    <BannerAlert />
                </div>
            </WebSocketContextProvider>
        </UserContextProvider>
    );
}

export default App;
